/* eslint-disable */
import * as React from 'react';
import './App.css';
import {BrowserRouter as Router, Navigate, Route, Routes} from 'react-router-dom';
import {Snackbar} from './interfaces/SnackbarData';
import theme from './theme';
import {Box, ThemeProvider} from '@mui/material';
import {AppContext, SnackbarContext} from './shared/store';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import ReleaseNote from './pages/ReleaseNote/ReleaseNotePage';
import TermsAndConditions from './pages/TermsAndConditions/TermsAndConditions';
import {appLayout, appLayoutWithFooter, mainComponentSx} from './styles';
import Navbar from './layouts/Navbar/Navbar';
import LoggedInHomepage from './pages/Home/Home';
import ProtectedRoute from "./auth/ProtectedRoute";
import PublicWelcome from './pages/PublicWelcome/PublicWelcome';
import Settings from './pages/Settings/Settings';
import {AuthApi, AuthProvider} from "./auth/AuthContext";
import {loadAuthImpl} from "./auth/loadAuth";
import Footer from "./layouts/Footer/Footer";
import Header from "./layouts/Header/Header";
import ManageSubscriptions from "./pages/ManageSubscriptions/ManageSubscriptions";
import TraceToggle from "./pages/TraceToggle/TraceToggle";
import OptimizationIngestScheduler from "./pages/OptimizationIngestScheduler/OptimizationIngestScheduler";
import BillingReturn from "./pages/BillingReturn/BillingReturn";
import NewRelicIntegration from "./pages/NewRelicIntegration/NewRelicIntegration";
import OrphanedSchedules from "./pages/OrphanedSchedules/OrphanedSchedules";

/* LOAD AUTH */
function AuthLoader({children}: { children: React.ReactNode }) {
    const [impl, setImpl] = React.useState<AuthApi | null>(null);
    React.useEffect(() => {
        loadAuthImpl().then(setImpl);
    }, []);
    if (!impl) return null;
    return <AuthProvider impl={impl}>{children}</AuthProvider>;
}

/* properties commonly shared between navigatable pages & components
*  e.g- keeping navbar or header hidden as displays change
*/
interface InternalRouteProps {
    exact?: boolean;
    showNavbar?: boolean;
    hideHeader?: boolean;
    path: string;
    component: any;
}

const InternalRoute = (props: InternalRouteProps) => {
    const {
        hideHeader, exact, path, showNavbar, component: Component,
    } = props;
    const snackbarData = React.useContext(SnackbarContext);


    const ComponentWithFooter = () => (
        <Box sx={appLayoutWithFooter}>

            <Box sx={mainComponentSx}>
                <Component/>
            </Box>
            <Footer/>
        </Box>
    );

    return (<Box display="flex" flexDirection="column" minHeight="100vh" width="100vw">
        {/* Header takes whatever height it wants; sticky prevents overlap */}
        <Box component="header" sx={{position: 'sticky', top: 0, width: "100vw", zIndex: 1100}}>
            {!hideHeader && <Header/>}
        </Box>

        {/* Everything else naturally starts below the header */}
        <Box display="flex" flexGrow={0} minHeight={0}>
            {showNavbar && <Navbar/>}
            <Box component="main" flexGrow={0} minWidth={0} overflow="auto">
                <ComponentWithFooter/>
            </Box>
        </Box>contrastText
    </Box>);
};


export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 0,
            staleTime: 5 * (60 * 1000),
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            retryOnMount: false,
        }
    }
})

/*Contains all Internal Route elements that users may navigate to*/
const ApplicationLayout = React.memo(() => {
    return (<Routes>
        {/* Public routes */}
        <Route path="/welcome" element={<InternalRoute path="/welcome" component={PublicWelcome}/>}/>

        <Route path="/releasenotes"
               element={<InternalRoute path="/releasenotes" component={ReleaseNote} showNavbar/>}/>

        <Route path="/termsandconditions"
               element={<InternalRoute path="/termsandconditions" component={TermsAndConditions} showNavbar/>}/>

        {/* Protected routes */}
        <Route path="/home" element={<ProtectedRoute>
            <InternalRoute path="/home" component={LoggedInHomepage} showNavbar/>
        </ProtectedRoute>}/>

        <Route path="/managesubscriptions" element={<ProtectedRoute>
            <InternalRoute path="/managesubscriptions" component={ManageSubscriptions} showNavbar/>
        </ProtectedRoute>}/>

        <Route path="/settings" element={<ProtectedRoute>
            <InternalRoute path="/settings" component={Settings} showNavbar/>
        </ProtectedRoute>}/>

        <Route path="/newRelicIntegration" element={<ProtectedRoute>
            <InternalRoute path="/newRelicIntegration" component={NewRelicIntegration} showNavbar/>
        </ProtectedRoute>}/>

        <Route path={"/billingReturn"} element={<ProtectedRoute>
            <InternalRoute path={"/billingReturn"} component={BillingReturn} hideHeader/>
        </ProtectedRoute>}/>

        <Route path="/OPTIMIZATION_INGEST_SCHEDULER" element={<ProtectedRoute>
            <InternalRoute path="/OPTIMIZATION_INGEST_SCHEDULER" component={OptimizationIngestScheduler} showNavbar/>
        </ProtectedRoute>}/>

        <Route path="/ORPHANED_SCHEDULES" element={<ProtectedRoute>
            <InternalRoute path="/ORPHANED_SCHEDULES" component={OrphanedSchedules} showNavbar/>
        </ProtectedRoute>}/>

        <Route path="/TRACE_TOGGLE" element={<ProtectedRoute>
            <InternalRoute path="/TRACE_TOGGLE" component={TraceToggle} showNavbar/>
        </ProtectedRoute>}/>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/home" replace/>}/>
    </Routes>)
})

/*define default properties for InternalRoute element*/
InternalRoute.defaultProps = {
    exact: false, hideHeader: false, secure: false, showNavbar: true,
}

/* contains all react elements for the newrelic-toolbox */
function App() {

    const [userViewIndex, setUserViewIndex] = React.useState<number>(1);
    const [snackbars, setSnackbars] = React.useState<Snackbar[]>([]);
    const [infoSnackbar, setInfoSnackbar] = React.useState<Snackbar | null>(null);


    const removeSnackbar = (id: Number) => {
        setSnackbars(prev => prev.filter(s => s.id !== id));
        if (infoSnackbar?.id === id) setInfoSnackbar(null);
    }

    const addSnackbar = ({
                             id, open, severity, boldAlertMsg, normalAlertMsg,
                         }: Snackbar) => {
        if (severity === 'info') {
            setInfoSnackbar({id, open, severity, boldAlertMsg, normalAlertMsg});
        } else {
            if (severity === 'success' || severity === 'error') {
                setInfoSnackbar(null);
            }

            setSnackbars((prev: Snackbar[]) => [...prev, {id, open, severity, boldAlertMsg, normalAlertMsg}])
        }

        if (severity === 'success') {
            setTimeout(() => {
                removeSnackbar(id);
            }, 2000);
        }
    }
    const contextValue = {
        addSnackbar, removeSnackbar, setInfoSnackbar, snackbars, setUserViewIndex, userViewIndex, infoSnackbar,
    }

    const appContextValue = React.useMemo(() => ({
        setUserViewIndex,
    }), []);

    const memoizedContextValue = React.useMemo(() => contextValue, [snackbars, infoSnackbar])
    return (  <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
            <Router>
                <Box sx={{ ...appLayout }}>
                    <AppContext.Provider value={appContextValue}>
                        <SnackbarContext.Provider value={memoizedContextValue}>
                            <AuthLoader>
                                <ApplicationLayout />
                            </AuthLoader>
                        </SnackbarContext.Provider>
                    </AppContext.Provider>
                </Box>
            </Router>
        </QueryClientProvider>
    </ThemeProvider>);
}

export default App;
